import { useTranslation } from "react-i18next";
import { Box, Step, StepContent, StepLabel, Typography } from "@mui/material";

import { Milestone } from "../../../../../../types/service.type";
import { MilestoneStatusEnum } from "../../../../../../enum/service.enum";
import { ContractState } from "../../../../../../types/contractState.types";
import { useMilestoneActions } from "../../../../../../hooks/useMilestoneActions";
import { useFormatMilestoneDueDate } from "../../../../../../hooks/useFormatMilestoneDueDate";
import { useReleaseContractPayment } from "../../../../../../api/mutations/useReleaseContractPayment";
import { useSendMilestonePaymentReminder } from "../../../../../../api/mutations/useSendMilestonePaymentReminder";

import { MilestoneStepIcon } from "./MilestoneStepIcon";
import { MilestoneActions } from "./MilestoneActions";
import { MilestoneStatusDisplay } from "./MilestoneStatusDisplay";
import { isMilestoneCanceled, getMilestoneBorderStyle } from "./utils";

interface MilestoneTimelineStepProps {
  milestone: Milestone;
  contractState: ContractState;
  milestoneIndex: number;
  milestonePrice: string;
  isLast: boolean;
  isProvider: boolean;
  isOwnershipLoading: boolean;
  contractId: number;
}

export const MilestoneTimelineStep = ({
  milestone,
  contractState,
  milestoneIndex,
  milestonePrice,
  isLast,
  isProvider,
  isOwnershipLoading,
  contractId,
}: MilestoneTimelineStepProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });

  const { name, deliveryDueDate, paymentDueDate, status } = milestone;
  const isPaid = status === MilestoneStatusEnum.Paid;
  const isPinning = status === MilestoneStatusEnum.Pinning;

  const formattedDeliveryDueDate = useFormatMilestoneDueDate(deliveryDueDate);
  const formattedPaymentDueDate = useFormatMilestoneDueDate(paymentDueDate || "");

  const { canRelease, canRemind, showStatus } = useMilestoneActions(
    milestone,
    contractState,
    isProvider,
    isOwnershipLoading
  );

  const borderLeft = getMilestoneBorderStyle(isLast, isPinning, isPaid);
  const canceled = isMilestoneCanceled(status, contractState.contractStatus);

  const { mutate: sendMilestonePaymentReminder, isPending: isMilestoneRemindPending } =
    useSendMilestonePaymentReminder(contractId);
  const handleMilestoneRemind = () => {
    sendMilestonePaymentReminder();
  };

  const { mutate: releasePayment, isPending: isReleasePaymentPending } = useReleaseContractPayment();
  const handleReleasePayment = () => {
    releasePayment(contractId);
  };

  return (
    <Step expanded={true} completed={isPaid}>
      <StepLabel StepIconComponent={MilestoneStepIcon}>
        <Typography sx={styles.milestoneNumberName}>
          {t("milestoneNumberName", { number: milestoneIndex, name })}
        </Typography>
      </StepLabel>
      <StepContent
        sx={{
          "&.MuiStepContent-root": {
            marginLeft: "15px",
            borderLeft,
          },
        }}
      >
        <Box sx={styles.milestoneContent}>
          <Box sx={styles.milestoneStatusContainer}>
            <Typography sx={styles.milestonePrice}>{milestonePrice}</Typography>
            <MilestoneStatusDisplay
              status={status}
              contractStatus={contractState.contractStatus}
              showStatus={showStatus}
            />
          </Box>
          <Typography sx={styles.milestoneDueDate(canceled)}>
            {paymentDueDate ? formattedPaymentDueDate : formattedDeliveryDueDate}
          </Typography>

          <MilestoneActions
            canRelease={canRelease}
            canRemind={canRemind}
            onReleasePayment={handleReleasePayment}
            onSendReminder={handleMilestoneRemind}
            isOwnershipLoading={isOwnershipLoading}
            isReleasePaymentPending={isReleasePaymentPending}
            isMilestoneRemindPending={isMilestoneRemindPending}
          />
        </Box>
      </StepContent>
    </Step>
  );
};

const styles = {
  milestoneNumberName: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  milestoneDueDate: (isCanceled: boolean) => ({
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    ...(isCanceled && {
      color: "var(--Error-700, #B42318)",
    }),
  }),
  milestonePrice: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  milestoneContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  milestoneStatusContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};
